/**
 * Centralized Environment Management
 * One source of truth for all environment-specific configurations
 */

export const ENV_CONFIG = {
  localhost: {
    baseUrl: 'http://localhost:3000',
    pythonBaseUrl: 'http://localhost:8001',
    domain:
      import.meta.env.VITE_FRONTEND_URL ||
      (typeof window !== 'undefined' ? window.location.origin : ''),
    images: `${
      import.meta.env.VITE_FRONTEND_URL ||
      (typeof window !== 'undefined' ? window.location.origin : '')
    }/images/`,
    assets: `${
      import.meta.env.VITE_FRONTEND_URL ||
      (typeof window !== 'undefined' ? window.location.origin : '')
    }/assets/`,
    frontend:
      import.meta.env.VITE_FRONTEND_URL ||
      (typeof window !== 'undefined' ? window.location.origin : ''),
  },
  staging: {
    baseUrl: 'https://staging-api.takefav.com',
    pythonBaseUrl: 'https://staging-api.takefav.com',
    domain: 'https://staging.takefav.com',
    images: 'https://staging.takefav.com/images/',
    assets: 'https://staging.takefav.com/assets/',
    frontend: 'https://staging.takefav.com',
  },
  production: {
    baseUrl: 'https://api.takefav.com',
    pythonBaseUrl: 'https://api.takefav.com',
    domain: 'https://www.takefav.com',
    images: 'https://www.takefav.com/images/',
    assets: 'https://www.takefav.com/assets/',
    frontend: 'https://www.takefav.com',
  },
}

/**
 * Detect current environment based on hostname
 */
export function getEnvironment() {
  if (typeof window === 'undefined') return 'production' // SSR fallback

  const hostname = window.location.hostname

  // Force staging mode for local development if needed
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    // Check if we want to force staging mode for testing
    const forceStaging = localStorage.getItem('forceStagingMode') === 'true'
    if (forceStaging) {
      console.log('🔧 Forcing staging mode for local development')
      return 'staging'
    }
    return 'localhost'
  }
  
  if (hostname === 'staging.takefav.com' || hostname.includes('staging-deploy.takefav-frontend.pages.dev') || hostname.includes('takefav-frontend.pages.dev')) return 'staging'
  if (hostname === 'www.takefav.com' || hostname === 'takefav.com') return 'production'
  return 'production' // fallback
}

/**
 * Get current environment config
 */
export function getCurrentEnvConfig() {
  const env = getEnvironment()
  return ENV_CONFIG[env]
}

/**
 * Get base URL for current environment (without /api)
 */
export function getBaseAPI() {
  const env = getEnvironment()
  return ENV_CONFIG[env].baseUrl
}

/**
 * Get Python base URL for current environment (without /api)
 */
export function getPythonAPI() {
  const env = getEnvironment()
  return ENV_CONFIG[env].pythonBaseUrl
}

/**
 * Get base image URL for current environment
 */
export function getImageBase() {
  const env = getEnvironment()
  return ENV_CONFIG[env].images
}

/**
 * Get base asset URL for current environment
 */
export function getAssetBase() {
  const env = getEnvironment()
  return ENV_CONFIG[env].assets
}

/**
 * Get domain for current environment
 */
export function getDomain() {
  const env = getEnvironment()
  return ENV_CONFIG[env].domain
}

/**
 * Get frontend URL for current environment
 */
export function getFrontendUrl() {
  const env = getEnvironment()
  return ENV_CONFIG[env].frontend
}

/**
 * Force staging mode for local development (useful for testing staging APIs)
 */
export function forceStagingMode() {
  localStorage.setItem('forceStagingMode', 'true')
  console.log('🔧 Staging mode forced for local development')
  window.location.reload()
}

/**
 * Reset to normal localhost mode
 */
export function resetToLocalhost() {
  localStorage.removeItem('forceStagingMode')
  console.log('🔧 Reset to localhost mode')
  window.location.reload()
}

/**
 * Check if staging mode is currently forced
 */
export function isStagingModeForced() {
  return localStorage.getItem('forceStagingMode') === 'true'
}

/**
 * Get complete image URL for given image path
 */
export function getImageUrl(imagePath: string): string {
  if (!imagePath) return ''

  const env = getEnvironment()

  // Remove leading slash if present
  const cleanPath = imagePath.startsWith('/') ? imagePath.slice(1) : imagePath

  // Use centralized config instead of hardcoded URLs
  return `${ENV_CONFIG[env].images}${cleanPath}`
}

/**
 * Get complete asset URL for given asset path
 */
export function getAssetUrl(assetPath: string): string {
  if (!assetPath) return ''

  const env = getEnvironment()

  // Remove leading slash if present
  const cleanPath = assetPath.startsWith('/') ? assetPath.slice(1) : assetPath

  // Use centralized config instead of hardcoded URLs
  return `${ENV_CONFIG[env].assets}${cleanPath}`
}

/**
 * Check if current environment is development
 */
export function isDevelopment() {
  return getEnvironment() === 'localhost'
}

/**
 * Check if current environment is staging
 */
export function isStaging() {
  return getEnvironment() === 'staging'
}

/**
 * Check if current environment is production
 */
export function isProduction() {
  return getEnvironment() === 'production'
}

/**
 * Get environment info for debugging
 */
export function getEnvironmentInfo() {
  const env = getEnvironment()
  return {
    environment: env,
    config: ENV_CONFIG[env],
    hostname: typeof window !== 'undefined' ? window.location.hostname : 'unknown',
    userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : 'unknown',
  }
}
